import { useFrame, useThree } from '@react-three/fiber';
import React, { FunctionComponent, useMemo } from 'react';
import { Color, Scene } from 'three';
import { useUserStore } from '@/zustand';
import { zapparCameraInstance } from './ZapparCameraWrapper';

/**
 * Owns what the desktop preview shows behind the content.
 *
 * A world-tracked scene has no camera feed worth showing — it is a 3D viewport — so it gets a flat sky.
 * Every other scene shows the camera texture, which for the preview is Designer's simulated room or the
 * sample face clip.
 *
 * ASSERTED EVERY FRAME rather than attached declaratively, for two reasons that both bite:
 * `ZapparCamera` attaches its texture with `attach="background"` on the same scene, and it mounts on a
 * LATER commit than anything here (it waits for launch), so it wins any attach-order race. And it
 * attaches exactly once and never re-attaches, so a component that merely overwrote the background on a
 * world scene would leave every later tracked scene on a flat colour with no feed behind it. Deciding
 * the whole answer here, every frame, is idempotent and immune to both.
 *
 * Priority 9: after the camera's own frame callback at -1, before `ArScene` renders at 10.
 */

/** The image preview's own backdrop colour, so the two previews sit in the same space. */
const SKY = '#dfe4ea';

/**
 * The sky for a dark room.
 *
 * It has to move with the floor: a dark floor under the light sky above reads as a lighting bug rather
 * than a choice, and the sky is most of what a world scene shows. Darker than the boards themselves, so
 * the floor still reads as the lit thing in the frame.
 */
const SKY_DARK = '#23262b';

interface IParentProps {
	/** Is the active scene one the preview orbits, rather than one it tracks? */
	isWorldPreviewScene: boolean;
}

const PreviewBackground: FunctionComponent<IParentProps> = ({ isWorldPreviewScene }) => {
	const rootScene = useThree(({ scene }) => scene);
	const isDark = useUserStore((state) => state.isPreviewRoomDark as boolean);
	const sky = useMemo(() => new Color(isDark ? SKY_DARK : SKY), [isDark]);

	useFrame(() => {
		// `useThree().scene` is the r3f ROOT scene; ArScene renders a NESTED one, and that nested scene
		// is the one whose background gets drawn. Its name is already load-bearing elsewhere — Lighting
		// takes it as `environmentSceneName`.
		const arScene = rootScene.getObjectByName('ArScene') as Scene | undefined;
		if (!arScene) return;

		const wanted = isWorldPreviewScene ? sky : zapparCameraInstance?.backgroundTexture ?? null;
		if (arScene.background !== wanted) arScene.background = wanted;
	}, 9);

	return null;
};

PreviewBackground.displayName = 'PreviewBackground';
export default PreviewBackground;
